import React, { useMemo, useEffect, useState } from "react";
import Calendar from "react-calendar";
import lightbox from "lightbox2";
import "lightbox2/dist/css/lightbox.min.css";
import "./EventCalendar.css";
import { BottomNav } from "../BottomNav";

const calendarId =
  "f4fe9f2af179e0fde0dc9c8124c40a4def43550701e868e249b787ab67cfef6c@group.calendar.google.com";
const apiKey = "AIzaSyDskidO6hwgiNT4nbC-lNfFtlHKm7isBic";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const toLocalDayKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

// Semana empezando en lunes (ES)
const startOfWeekMonday = (d) => {
  const day = d.getDay(); // 0 dom, 1 lun...
  const diff = (day === 0 ? -6 : 1 - day);
  return addDays(d, diff);
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [query]);

  return matches;
}

const formatTime = (d) =>
  d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

const formatEventTime = (ev) => {
  if (ev.isAllDay) return "Todo el día";
  const startStr = formatTime(ev.date);
  if (!ev.endDate) return startStr;
  const endStr = formatTime(ev.endDate);
  // Si por algún motivo coinciden, no duplicamos el texto.
  if (startStr === endStr) return startStr;
  return `${startStr} - ${endStr}`;
};

// Solo hora de inicio, para los chips compactos del calendario (vista mes).
const formatEventStartTime = (ev) => {
  if (ev.isAllDay) return "Todo el día";
  return formatTime(ev.date);
};

export const EventCalendar = (props) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
    const { user } = props;

  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMonth, setShowMonth] = useState(false); // modal/calendario en móvil

  const fetchEvents = async () => {
    try {
      const from = new Date();
      from.setHours(0, 0, 0, 0);

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?key=${apiKey}&timeMin=${from.toISOString()}&singleEvents=true&orderBy=startTime`
      );
      const data = await response.json();

      if (data.items) {
        const fetchedEvents = data.items
          .map((event) => {
            const description = event.description || "";
            const thumbUrlMatch = description.match(/img-thumb:(https?:\/\/\S+)/);
            const fullUrlMatch = description.match(/img-full:(https?:\/\/\S+)/);

            const thumbUrl = thumbUrlMatch ? thumbUrlMatch[1] : null;
            const fullUrl = fullUrlMatch ? fullUrlMatch[1] : null;

            const cleanDescription = description
              .replace(/img-thumb:(https?:\/\/\S+)/g, "")
              .replace(/img-full:(https?:\/\/\S+)/g, "")
              .replace(/<br\s*\/?>/g, "\n")
              .trim();

            const start = new Date(event.start.dateTime || event.start.date);
            const endRaw = event.end?.dateTime || event.end?.date || null;
            const end = endRaw ? new Date(endRaw) : null;

            return {
              id: event.id,
              date: start,
              endDate: end,
              title: event.summary,
              description: cleanDescription,
              location: event.location || "Sin ubicación",
              thumbUrl,
              fullUrl,
              isAllDay: !!event.start.date && !event.start.dateTime,
            };
          })
          .sort((a, b) => a.date - b.date);

        setEvents(fetchedEvents);
      }
    } catch (error) {
      console.error("Error al obtener eventos:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
    lightbox.option({ resizeDuration: 200, wrapAround: true });
  }, []);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const key = toLocalDayKey(ev.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return map;
  }, [events]);

  const selectedDayEvents = useMemo(() => {
    return eventsByDay.get(toLocalDayKey(selectedDate)) || [];
  }, [eventsByDay, selectedDate]);

  // Próximos 7 días (para agenda rápida)
  const upcoming = useMemo(() => {
    const now = new Date();
    const limit = addDays(now, 60); // ajusta si quieres
    return events.filter((e) => e.date >= now && e.date <= limit);
  }, [events]);

  // Semana visible en móvil
  const weekDays = useMemo(() => {
    const start = startOfWeekMonday(selectedDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [selectedDate]);

  const hasEvents = (date) => (eventsByDay.get(toLocalDayKey(date)) || []).length;

  return (

    
    <div className="ec-wrap">
            <BottomNav I18n={props.I18n} user={user} />
      {/* ---------- MOBILE UI ---------- */}
      {isMobile ? (
        <>
          <div className="ec-mobile-header">
            <div className="ec-mobile-month">
              <div className="ec-mobile-month-title">
                {selectedDate.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
              </div>
              <button className="ec-month-btn" onClick={() => setShowMonth(true)}>
                Ver mes
              </button>
            </div>

            <div className="ec-week-strip">
              {weekDays.map((d) => {
                const count = hasEvents(d);
                const active = toLocalDayKey(d) === toLocalDayKey(selectedDate);

                return (
                  <button
                    key={toLocalDayKey(d)}
                    className={`ec-week-day ${active ? "is-active" : ""}`}
                    onClick={() => setSelectedDate(d)}
                  >
                    <div className="ec-week-dow">
                      {d.toLocaleDateString("es-ES", { weekday: "short" }).replace(".", "")}
                    </div>
                    <div className="ec-week-num">{d.getDate()}</div>
                    {count > 0 && <div className="ec-week-badge">{count}</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ec-agenda">
            <div className="ec-agenda-head">
              <h2 className="ec-title">
                {selectedDate.toLocaleDateString("es-ES", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </h2>
              <div className="ec-sub">
                {selectedDayEvents.length
                  ? `${selectedDayEvents.length} evento(s)`
                  : "No hay eventos"}
              </div>
            </div>

            {/* Eventos del día */}
            {selectedDayEvents.length ? (
              <div className="ec-event-list">
                {selectedDayEvents.map((event) => (
                  <div key={event.id} className="ec-event-card">
                    <div className="ec-event-top">
                      <div className="ec-event-title">{event.title}</div>
                      <div className="ec-event-time">{formatEventTime(event)}</div>
                    </div>

                    <div className="ec-event-meta">
                      <div className="ec-meta-row">
                        <span className="ec-meta-label">📍</span>
                        <span className="ec-meta-value">{event.location}</span>
                      </div>
                    </div>

                    {event.thumbUrl && (
                      <a
                        href={event.fullUrl || event.thumbUrl}
                        data-lightbox="event-images"
                        data-title={event.title}
                        className="ec-img-link"
                      >
                        <img
                          src={event.thumbUrl}
                          alt={event.title}
                          className="ec-event-img"
                          loading="lazy"
                        />
                      </a>
                    )}

                    {event.description && (
                      <div className="ec-desc">
                        {event.description.split("\n").map((line, idx) => (
                          <span key={idx}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="ec-empty">
                Consejo: revisa “Ver mes” para ver más días con eventos.
              </div>
            )}

            {/* Próximos eventos (mini-lista) */}
            <div className="ec-upcoming">
              <div className="ec-upcoming-title">Próximos</div>
              {upcoming.slice(0, 5).map((ev) => (
                <button
                  key={ev.id}
                  className="ec-upcoming-item"
                  onClick={() => setSelectedDate(startOfDay(ev.date))}
                >
                  <div className="ec-upcoming-date">
                    <div className="ec-upcoming-day">{ev.date.getDate()}</div>
                    <div className="ec-upcoming-mon">
                      {ev.date.toLocaleDateString("es-ES", { month: "short" }).replace(".", "")}
                    </div>
                  </div>
                  <div className="ec-upcoming-info">
                    <div className="ec-upcoming-name">{ev.title}</div>
                    <div className="ec-upcoming-sub">
                      {formatEventTime(ev)} · {ev.location}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Modal “Ver mes” */}
          {showMonth && (
            <div className="ec-modal-backdrop" onClick={() => setShowMonth(false)}>
              <div className="ec-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ec-modal-head">
                  <div className="ec-modal-title">Calendario</div>
                  <button className="ec-modal-close" onClick={() => setShowMonth(false)}>
                    ✕
                  </button>
                </div>

                <Calendar
                  onChange={(d) => {
                    setSelectedDate(d);
                    setShowMonth(false);
                  }}
                  value={selectedDate}
                  locale="es-ES"
                  tileClassName={({ date, view }) => {
                    if (view !== "month") return null;
                    const n = hasEvents(date);
                    return n ? "ec-day-has-events" : null;
                  }}
                  tileContent={({ date, view }) => {
                    if (view !== "month") return null;

                    const n = hasEvents(date);
                    if (!n) return null;

                    const dotsToShow = Math.min(n, 3);
                    const extra = n - dotsToShow;

                    return (
                      <div className="ec-dots">
                        {Array.from({ length: dotsToShow }).map((_, i) => (
                          <span key={i} className="ec-dot" />
                        ))}
                        {extra > 0 && <span className="ec-dot-more">+{extra}</span>}
                      </div>
                    );
                  }}

                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* ---------- DESKTOP (mes + chips) ---------- */
        <div className="ec-desktop">
          <div className="ec-calendar-card">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              locale="es-ES"
              tileClassName={({ date, view }) => {
                if (view !== "month") return null;
                return hasEvents(date) ? "ec-day-has-events" : null;
              }}
              tileContent={({ date, view }) => {
                if (view !== "month") return null;
                const dayEvents = eventsByDay.get(toLocalDayKey(date)) || [];
                if (!dayEvents.length) return null;

                const visible = dayEvents.slice(0, 2);
                const remaining = dayEvents.length - visible.length;

                return (
                  <div className="ec-day-events">
                    {visible.map((ev) => (
                      <div key={ev.id} className="ec-chip" title={ev.title}>
                        <span className="ec-chip-time">{formatEventStartTime(ev)}</span>
                        <span className="ec-chip-title">{ev.title}</span>
                      </div>
                    ))}
                    {remaining > 0 && <div className="ec-more">+{remaining}</div>}
                  </div>
                );
              }}
            />
          </div>

          <div className="ec-events-panel">
            <h2 className="ec-title">
              {selectedDate.toLocaleDateString("es-ES", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </h2>

            {selectedDayEvents.length === 0 ? (
              <div className="ec-empty">No hay eventos este día.</div>
            ) : (
              selectedDayEvents.map((event) => (
                <div key={event.id} className="ec-event-card">
                  <div className="ec-event-top">
                    <div className="ec-event-title">{event.title}</div>
                    <div className="ec-event-time">{formatEventTime(event)}</div>
                  </div>

                  <div className="ec-meta-row">
                    <span className="ec-meta-label">📍</span>
                    <span className="ec-meta-value">{event.location}</span>
                  </div>

                  {event.thumbUrl && (
                    <a
                      href={event.fullUrl || event.thumbUrl}
                      data-lightbox="event-images"
                      data-title={event.title}
                      className="ec-img-link"
                    >
                      <img
                        src={event.thumbUrl}
                        alt={event.title}
                        className="ec-event-img"
                        loading="lazy"
                      />
                    </a>
                  )}

                  {event.description && (
                    <div className="ec-desc">
                      {event.description.split("\n").map((line, idx) => (
                        <span key={idx}>
                          {line}
                          <br />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
